import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Grid from "@mui/material/Grid"
import Select from "../select/select"
import DatePicker from "../datePicker/datePicker"
import InputText from "../inputText/inputText"
import Checkbox from "../checkbox/checkbox"
import Button from "../button/button"
import IconsColor from "../../util/iconsColor"
import styles from "../../asset/styles/quotation/newQuotation.module.scss"
import { agent } from "../../apiClient/api"
import { GenericQuotationSourceTypeItem, GenericQuotationStatusItem, QuotationBasicDetailsPutBody } from "../../models/axiosModel"
import { useSnackbar } from "../../context/snackbarContext"
import { QuotationContextType, useQuotation } from "../../context/quotationContext"
import { useAgencyAgentContext } from "context/agencyAgentContext"
import { toTitleCase } from "util/textcase"
import { getActiveCityOptions, CityGenericItem } from "util/cityOptionsUtil"
import {getQuotationStatusIdFromQuotationStatusName, QuotationStatusVisualVariant} from "./quotationStatusVariant";

/** `draft` → status id 1. `created` → status id 2. `finalize` →  status id 3. */
export type QuotationBasicSaveIntent = QuotationStatusVisualVariant

export interface CityNightRow {
  id: string
  city: string
  nights: string
}

export interface ChildDetailRow {
  id: string
  age: string
  withBed: boolean
}

interface BasicDetailsValidationErrors {
  agency?: string
  agent?: string
  startDate?: string
  noOfNights?: string
  adults?: string
  cityRows: Record<string, { city?: string; nights?: string }>
  totalNights?: string
}

export const createDefaultCityRow = (): CityNightRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  city: "",
  nights: "1",
})

const createDefaultChildRow = (): ChildDetailRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  age: "5",
  withBed: false,
})

const FALLBACK_QUOTATION_SOURCE_TYPES: GenericQuotationSourceTypeItem[] = [
  { quotation_source_type_id: 1, quotation_source_type_name: "Call" },
  { quotation_source_type_id: 2, quotation_source_type_name: "Email" },
  { quotation_source_type_id: 3, quotation_source_type_name: "WhatsApp" },
  { quotation_source_type_id: 4, quotation_source_type_name: "Other" },
]

const isQuotationSourceOtherName = (name: string): boolean =>
  String(name || "").toLowerCase().trim().includes("other")

const mapQuotationSourceTypesToOptions = (list: GenericQuotationSourceTypeItem[]) =>
  list
    .map((item) => ({
      label: String(item.quotation_source_type_name || ""),
      value: String(item.quotation_source_type_id ?? ""),
    }))
    .filter((item) => item.value && item.label)

const isSourceOtherSelectOption = (opt: { label?: string }) =>
  isQuotationSourceOtherName(String(opt.label || ""))

const resolveSourceTypeLabel = (
  typeId: string,
  typeNameFromApi: string,
  options: { label: string; value: string }[]
): string => {
  if (typeNameFromApi) return typeNameFromApi
  const fromOptions = options.find((opt) => opt.value === typeId)
  if (fromOptions?.label) return fromOptions.label
  const fromFallback = FALLBACK_QUOTATION_SOURCE_TYPES.find(
    (item) => String(item.quotation_source_type_id) === typeId
  )
  return fromFallback?.quotation_source_type_name || ""
}

const getComputedEndDate = (startDate: string, noOfNights: string): string => {
  if (!startDate) return "N/A"
  const nights = Math.max(1, Number(noOfNights) || 1)
  const baseDate = new Date(`${startDate}T00:00:00`)
  if (Number.isNaN(baseDate.getTime())) return "N/A"
  baseDate.setDate(baseDate.getDate() + nights)
  return baseDate.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })
}

interface QuotationBasicDetailsProps {
  quotationId?: string
  onSaved?: (quotationId: string) => void
  /** Save returns the persisted quotation id on success, null on failure. */
  onBindSaveHandler?: (handler: (intent?: QuotationBasicSaveIntent) => Promise<string | null>) => void
  /** Binds a refetch handler so the parent can force-reload Basic Details from the API
   *  (this step stays mounted via display:none, so it won't auto-refetch on navigation). */
  onBindRefetchHandler?: (handler: () => Promise<void>) => void
  quotationNameOverride?: string
  onHeaderMetaChange?: (meta: { quotationName: string; staCode: string; }) => void
  flagQuotationBasicDetailsApiRespReady: boolean;
}

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const QuotationBasicDetails: React.FC<QuotationBasicDetailsProps> = ({ quotationId = "", onSaved, onBindSaveHandler, onBindRefetchHandler, quotationNameOverride, onHeaderMetaChange, flagQuotationBasicDetailsApiRespReady }) => {

  const { showError, showSuccess } = useSnackbar()

  const quotationIdRef = useRef(quotationId)

  useEffect(() => {
    quotationIdRef.current = quotationId
  }, [quotationId])

  const {
    agencyId, setAgencyId,
    agentId, setAgentId,
    staCode, setStaCode,
    setCreatedBy,
    quotationName, setQuotationName,
    startDate, setStartDate,
    noOfNights, setNoOfNights,
    selectedCities, setSelectedCities,
    adults, setAdults,
    extraBed, setExtraBed,
    children, setChildren,
    childDetails, setChildDetails,
    rooms, setRooms,
    flagVisaFee, setFlagVisaFee,
    visaFeePerPax, setVisaFeePerPax,
    foc, setFoc,
    focPaxNumber, setFocPaxNumber,
    setCityOptions,
    quotationBasicDetailsApiResponse
  } = useQuotation() as QuotationContextType;

  const {
    setAgenciesOptions,
    setAgentsOptions,
    setAllAgencies,
    setAllAgents,
    allAgents,
    agenciesOptions,
    agentsOptions
  } = useAgencyAgentContext();

  const finalizeStatusIdRef = useRef(2)
  // const [selectedAgencyId, setSelectedAgencyId] = useState("")
  // const [agencyOptions, setAgencyOptions] = useState<{ label: string; value: string }[]>([])
  // const [agentOptions, setAgentOptions] = useState<{ label: string; value: string }[]>([])
  // const [allAgents, setAllAgents] = useState<Array<{
  //   agent_id: string
  //   agent_name: string
  //   agency_id: string
  //   agency_name: string
  //   contact_number?: string | null
  //   email?: string | null
  // }>>([])
  // const [cities, setCities] = useState<CityNightRow[]>([createDefaultCityRow()])
  const [cityOptionsState, setCityOptionsState] = useState<{ label: string; value: string }[]>([])
  const [cityGenericList, setCityGenericList] = useState<CityGenericItem[]>([])
  const [sourceOptions, setSourceOptions] = useState<{ label: string; value: string }[]>(
    () => mapQuotationSourceTypesToOptions(FALLBACK_QUOTATION_SOURCE_TYPES)
  )
  const [quotationSourceTypeId, setQuotationSourceTypeId] = useState("")
  const [quotationSourceOther, setQuotationSourceOther] = useState("")
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [apiResponseMessage, setApiResponseMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [cityFieldTouched, setCityFieldTouched] = useState<Record<string, { city?: boolean; nights?: boolean }>>({})

  useEffect(() => {
    if (quotationNameOverride === undefined) return
    // setQuotationName(quotationNameOverride)
  }, [quotationNameOverride])

  useEffect(() => {
    if (!onHeaderMetaChange) return
    onHeaderMetaChange({ quotationName, staCode })
  }, [quotationName, staCode, onHeaderMetaChange])

  const toNumber = (value: string, fallback = 0): number => {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? fallback : parsed
  }

  const toNullableUuid = (value: string): string | null => (uuidRegex.test(value) ? value : null)

  const totalCityNights = useMemo(() => {
    return selectedCities.reduce((sum, item) => sum + (Number(item.nights) || 0), 0)
  }, [selectedCities])

  const computedEndDate = useMemo(() => getComputedEndDate(startDate, noOfNights), [startDate, noOfNights])

  // Active cities only, plus this row's own city if it has since been deactivated (frozen but visible),
  // plus a legacy fallback label for a city_id no longer present at all in the generic-data list.
  const getRowCityOptions = useCallback(
    (currentCityId: string) => {
      const activeOptions = getActiveCityOptions(cityGenericList, currentCityId).map((option) => ({
        ...option,
        label: toTitleCase(option.label),
      }))
      const existsInGenericList = cityGenericList.some((city) => String(city.city_id) === currentCityId)
      if (currentCityId && !existsInGenericList) {
        const legacyFallback = cityOptionsState.find((option) => option.value === currentCityId)
        if (legacyFallback) return [...activeOptions, legacyFallback]
      }
      return activeOptions
    },
    [cityGenericList, cityOptionsState]
  )

  useEffect(() => {
    let isMounted = true

    const fetchAgentOptions = async () => {
      try {
        const [agencyAgentResponse, genericResponse] = await Promise.all([
          agent.QuotationAgencyAgentListAPI.get(true),
          agent.GenericGlobalAPI.get(true),
        ])
        const agencyList = agencyAgentResponse?.status && Array.isArray(agencyAgentResponse?.data) ?
          agencyAgentResponse?.data.map((value) => {
            return {
              agency_id: value.agency_id ?? "",
              agency_name: toTitleCase(value.agency_name ?? "")
            }
          })
          : [];
        const normalizedAgents =
          agencyAgentResponse?.status && Array.isArray(agencyAgentResponse?.data)
            ? agencyAgentResponse.data.flatMap((agencyItem: any) => {
              const agencyId = String(agencyItem?.agency_id || "")
              const agencyName = String(agencyItem?.agency_name || "")
              const agents = Array.isArray(agencyItem?.agents) ? agencyItem.agents : []
              return agents
                .map((agentItem: any) => ({
                  agent_id: String(agentItem?.agent_id || ""),
                  agent_name: toTitleCase(String(agentItem?.agent_name || "")),
                  agency_id: agencyId,
                  agency_name: toTitleCase(agencyName),
                  contact_number: agentItem?.contact_number ?? null,
                  email: agentItem?.email ?? null,
                }))
                .filter((item: any) => item.agent_id && item.agent_name && item.agency_id)
            })
            : []
        const agencies =
          agencyAgentResponse?.status && Array.isArray(agencyAgentResponse?.data)
            ? agencyAgentResponse.data
              .map((agencyItem: any) => ({
                value: String(agencyItem?.agency_id || ""),
                label: toTitleCase(String(agencyItem?.agency_name || "")),
              }))
              .filter((item: any) => item.value && item.label)
            : []

        const genericRoot = Array.isArray(genericResponse?.data) ? genericResponse.data[0] : null
        const genericCityList = genericRoot?.city
        const rawCityList: CityGenericItem[] = Array.isArray(genericCityList) ? genericCityList : []
        const cityOptions = rawCityList.map((city) => ({
          label: toTitleCase(city.city_name),
          value: toTitleCase(String(city.city_id)),
        }))

        const statusList: GenericQuotationStatusItem[] | undefined = genericRoot?.quotation_status_id
        if (Array.isArray(statusList) && statusList.length >= 2) {
          const sorted = [...statusList].sort((a, b) => a.quotation_status_id - b.quotation_status_id)
          finalizeStatusIdRef.current = sorted[1].quotation_status_id
        } else {
          finalizeStatusIdRef.current = 2
        }

        const sourceTypeList: GenericQuotationSourceTypeItem[] =
          Array.isArray(genericRoot?.quotation_source_type) && genericRoot.quotation_source_type.length > 0
            ? genericRoot.quotation_source_type
            : FALLBACK_QUOTATION_SOURCE_TYPES

        if (isMounted) {
          setAllAgents(normalizedAgents)
          setAllAgencies(agencyList);
          setAgenciesOptions(agencies)
          setAgentsOptions([])
          setCityOptionsState(cityOptions)
          setCityGenericList(rawCityList)
          setCityOptions(cityOptions)
          setSourceOptions(mapQuotationSourceTypesToOptions(sourceTypeList))
        }
      } catch (error) {
        if (isMounted) {
          setAllAgents([])
          setAllAgencies([])
          setAgenciesOptions([])
          setAgentsOptions([])
          setCityOptionsState([])
          setCityGenericList([])
          setSourceOptions(mapQuotationSourceTypesToOptions(FALLBACK_QUOTATION_SOURCE_TYPES))
          finalizeStatusIdRef.current = 2
        }
      }
    }

    fetchAgentOptions()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!agencyId && agentId) {
      const matchedAgent = allAgents.find((item) => item.agent_id === agentId)
      if (matchedAgent?.agency_id) setAgencyId(matchedAgent.agency_id)
    }
  }, [agencyId, agentId, allAgents, setAgencyId])

  useEffect(() => {
    // Wait until the agent master list has loaded. Clearing the selection before that would wipe
    // a hydrated agentId during the load race → false "Agent is required" on a saved quotation.
    if (allAgents.length === 0) return

    if (!agencyId) {
      setAgentsOptions([])
      // Only drop the agent if it belongs to NO agency (truly invalid). If it does belong to one,
      // agency derivation (the effect above) is pending and will set the scope momentarily.
      if (agentId && !allAgents.some((item) => item.agent_id === agentId)) {
        setAgentId("")
      }
      return
    }
    const scoped = allAgents
      .filter((item) => item.agency_id === agencyId)
      .map((item) => ({ label: item.agent_name, value: item.agent_id }))
    setAgentsOptions(scoped)
    if (agentId && !scoped.some((opt) => opt.value === agentId)) {
      setAgentId("")
    }
  }, [agencyId, allAgents, agentId, setAgentId, setAgentsOptions])
  
  const syncChildDetails = useCallback((childCount: number) => {
    setChildDetails((prev) => {
      if (childCount <= 0) return []
      if (prev.length === childCount) return prev
      if (prev.length > childCount) return prev.slice(0, childCount)
      return [...prev, ...Array.from({ length: childCount - prev.length }, createDefaultChildRow)]
    })
  }, [setChildDetails]);

  const getQuotationBasicDetailsApiResponseHandler = useCallback((response: any) => {
    const details = response?.status ? response?.data : null
    if (!details?.quotation) return

    const quotation = details.quotation
    setStaCode(quotation.sta_code || "")
    setQuotationName(quotation.quotation_name || "")
    setCreatedBy(quotation.created_by || "")
    setAgencyId((quotation as any).agency_id || "")
    setAgentId(quotation.agent_id || "")
    setStartDate(quotation.start_date || "")
    setNoOfNights(String(quotation.number_of_nights ?? 1))
    setAdults(String(quotation.number_of_adults ?? 0))
    setExtraBed(String(quotation.number_of_extra_bed ?? 0))
    const numberOfChildren = Number(quotation.number_of_children ?? 0)
    setChildren(String(numberOfChildren))
    setRooms(String(quotation.number_of_room ?? 1))
    setFlagVisaFee(Boolean(quotation.flag_include_visa_fee))
    setFoc(Boolean(quotation.flag_foc))
    setVisaFeePerPax(String(quotation.visa_fee_per_pax ?? 0))
    setFocPaxNumber(String(quotation.foc_pax_number ?? 0))

    const rawSourceTypeId = quotation.quotation_source_type_id
    const sourceTypeId =
      rawSourceTypeId != null && String(rawSourceTypeId).trim() !== ""
        ? String(rawSourceTypeId)
        : ""
    setQuotationSourceTypeId(sourceTypeId)

    const sourceTypeLabel = resolveSourceTypeLabel(
      sourceTypeId,
      String(quotation.quotation_source_type_name ?? "").trim(),
      sourceOptions
    )
    const trimmedSourceOther = String(quotation.quotation_source_other ?? "").trim()
    setQuotationSourceOther(
      sourceTypeId && isQuotationSourceOtherName(sourceTypeLabel) ? trimmedSourceOther : ""
    )

    if (Array.isArray(details.children) && details.children.length > 0) {
      const childrenRows = [...details.children]
        .sort((a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0))
        .map((childItem) => ({
          id: childItem.quotation_children_id || createDefaultChildRow().id,
          age: String(childItem.age ?? ""),
          withBed: Boolean(childItem.flag_with_bed),
        }))
      setChildDetails(childrenRows)
    } else {
      syncChildDetails(numberOfChildren)
    }

    if (Array.isArray(details.cities) && details.cities.length > 0) {
      const cityRows = [...details.cities]
        .sort((a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0))
        .map((cityItem) => ({
          id: cityItem.quotation_city_id || createDefaultCityRow().id,
          city: String(cityItem.city_id ?? ""),
          nights: String(cityItem.number_of_nights ?? 0),
        }))
      setSelectedCities(cityRows)

      setCityOptionsState((prev) => {
        const dynamicCityOptions = [...prev]
        cityRows.forEach((row) => {
          const exists = dynamicCityOptions.some((option) => option.value === row.city)
          if (!exists && row.city) {
            dynamicCityOptions.push({
              label: `City ${row.city}`,
              value: row.city,
            })
          }
        })
        return dynamicCityOptions
      })
    }
  }, [
    setAdults, setAgencyId, setAgentId, setChildDetails, setChildren, setCreatedBy, setExtraBed, setFlagVisaFee, setFoc, setFocPaxNumber, setNoOfNights, setQuotationName, setRooms, setSelectedCities, setStaCode, setStartDate, setVisaFeePerPax, sourceOptions, syncChildDetails
  ]);

  // Pull Basic Details from the API into context. Reads the id from a ref so the callback
  // stays stable (empty deps) and can be reused both on mount and for parent-triggered refetch.
  const fetchQuotationBasicDetails = useCallback(async (): Promise<void> => {
    const id = quotationIdRef.current
    if (!id) return
    try {
      const response = await agent.QuotationBasicDetailsAPI.get(id)
      getQuotationBasicDetailsApiResponseHandler(response);
    } catch (error) {
      // Preserve default values if API fails
    }
  }, [quotationIdRef, getQuotationBasicDetailsApiResponseHandler])

  useEffect(() => {
    if (flagQuotationBasicDetailsApiRespReady === true) {
      getQuotationBasicDetailsApiResponseHandler(quotationBasicDetailsApiResponse);
    }
  }, [flagQuotationBasicDetailsApiRespReady, getQuotationBasicDetailsApiResponseHandler, quotationBasicDetailsApiResponse])

  // Fetch on mount / when the quotation id changes.
  // useEffect(() => {
  //   if (!quotationId) return
  //   fetchQuotationBasicDetails()
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [quotationId])

  // Expose the refetch handler to the parent (NewQuotation) for step-navigation refresh.
  useEffect(() => {
    if (!onBindRefetchHandler) return
    onBindRefetchHandler(fetchQuotationBasicDetails)
  }, [onBindRefetchHandler, fetchQuotationBasicDetails])

  const handleNightsChange = (value: string, setter: React.Dispatch<React.SetStateAction<string>>) => {
    if (value === "") {
      setter("")
      return
    }
    const numericValue = Number(value)
    if (Number.isNaN(numericValue) || numericValue < 0) return
    setter(String(Math.floor(numericValue)))
  }

  const updateCityRow = (id: string, key: "city" | "nights", value: string) => {
    setCityFieldTouched((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: true },
    }))
    setSelectedCities((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)))
  }

  const getValidationErrors = (): BasicDetailsValidationErrors => {
    const errors: BasicDetailsValidationErrors = {
      cityRows: {},
    }

    if (!agencyId) {
      errors.agency = "Agency is required"
    }
    if (!agentId) {
      errors.agent = "Agent is required"
    }
    if (!startDate) {
      errors.startDate = "Start Date is required"
    }

    const nightsValue = Number(noOfNights)
    if (!noOfNights.trim()) {
      errors.noOfNights = "No. of Nights is required"
    } else if (Number.isNaN(nightsValue) || nightsValue <= 0) {
      errors.noOfNights = "No. of Nights must be greater than 0"
    }

    const adultsValue = Number(adults)
    if (!adults.trim()) {
      errors.adults = "No. of Adults is required"
    } else if (Number.isNaN(adultsValue) || adultsValue <= 0) {
      errors.adults = "No. of Adults must be greater than 0"
    }

    selectedCities.forEach((row) => {
      const rowErrors: { city?: string; nights?: string } = {}
      if (!row.city) {
        rowErrors.city = "City is required"
      }
      const rowNights = Number(row.nights)
      if (!row.nights.trim()) {
        rowErrors.nights = "Nights is required"
      } else if (Number.isNaN(rowNights) || rowNights <= 0) {
        rowErrors.nights = "Nights must be greater than 0"
      }
      if (rowErrors.city || rowErrors.nights) {
        errors.cityRows[row.id] = rowErrors
      }
    })

    if (!errors.noOfNights && totalCityNights !== nightsValue) {
      errors.totalNights = "No. of Nights must exactly match the sum of all city nights"
      // errors.noOfNights = errors.noOfNights || errors.totalNights
      // errors.noOfNights = "No. of Nights must exactly match the sum of all city nights"
    }

    return errors
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const validationErrors = useMemo(() => getValidationErrors(), [
    agencyId,
    agentId,
    startDate,
    noOfNights,
    adults,
    selectedCities,
    totalCityNights,
  ])

  const hasValidationErrors = (errors: BasicDetailsValidationErrors): boolean => {
    return Boolean(
      errors.agent ||
      errors.agency ||
      errors.startDate ||
      errors.noOfNights ||
      errors.adults ||
      errors.totalNights ||
      Object.keys(errors.cityRows).length
    )
  }

  const addCity = () => {
    const newRow = createDefaultCityRow()
    setSelectedCities((prev) => [...prev, newRow])
    setCityFieldTouched((prev) => ({ ...prev, [newRow.id]: { city: false, nights: false } }))
  }

  const removeCity = (id: string) => {
    setSelectedCities((prev) => {
      if (prev.length <= 1) return prev
      return prev.filter((row) => row.id !== id)
    })
    setCityFieldTouched((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleChildrenCountChange = (value: string) => {
    if (value === "") {
      setChildren("")
      setChildDetails([])
      return
    }
    const numericValue = Number(value)
    if (Number.isNaN(numericValue) || numericValue < 0) return
    const sanitizedValue = String(Math.floor(numericValue))
    setChildren(sanitizedValue)
    syncChildDetails(Number(sanitizedValue))
  }

  const handleAdultsCountChange = (value: string) => {
    if (value === "") {
      setAdults("")
      setRooms("")
      return
    }
    const numericValue = Number(value)
    if (Number.isNaN(numericValue) || numericValue < 0) return

    const sanitizedAdults = Math.floor(numericValue)
    setAdults(String(sanitizedAdults))
    setRooms(String(Math.ceil(sanitizedAdults / 2)))
  }

  const updateChildDetailRow = (id: string, key: "age" | "withBed", value: string | boolean) => {
    setChildDetails((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)))
  }

  const saveBasicDetails = useCallback(async (intent?: QuotationBasicSaveIntent): Promise<string | null> => {
    const currentValidationErrors = getValidationErrors()
    if (hasValidationErrors(currentValidationErrors)) {
      setShowValidationErrors(true);
      if (currentValidationErrors.noOfNights || currentValidationErrors.totalNights) {
        showError("No. of Nights must exactly match the sum of all city nights")
      }
      return null
    }

    setShowValidationErrors(false)
    setApiResponseMessage(null)

    const quotationStatusForPayload = getQuotationStatusIdFromQuotationStatusName(intent ?? "draft")
    const selectedAgent = allAgents.find((item) => item.agent_id === agentId)

    const payload: QuotationBasicDetailsPutBody = {
      quotation_id: quotationId || null,
      sta_code: staCode,
      quotation_name: quotationName,
      agent_name: String(selectedAgent?.agent_name || "").trim(),
      start_date: startDate || "",
      number_of_nights: Math.max(0, toNumber(noOfNights, 0)),
      agency_id: agencyId || undefined,
      agent_id: agentId || "",
      number_of_adults: Math.max(0, toNumber(adults, 0)),
      number_of_extra_bed: Math.max(0, toNumber(extraBed, 0)),
      number_of_children: Math.max(0, toNumber(children, 0)),
      number_of_room: Math.max(0, toNumber(rooms, 0)),
      flag_include_visa_fee: flagVisaFee,
      flag_foc: foc,
      foc_pax_number: Math.max(0, toNumber(focPaxNumber, 0)),
      quotation_status_id: quotationStatusForPayload,
      visa_fee_per_pax: Math.max(0, toNumber(visaFeePerPax, 0)),
      cities: selectedCities.map((city, index) => ({
        quotation_city_id: toNullableUuid(city.id),
        city_id: toNumber(city.city, 0),
        number_of_nights: Math.max(0, toNumber(city.nights, 0)),
        sequence_number: index + 1,
      })),
      children: childDetails.map((child, index) => ({
        quotation_children_id: toNullableUuid(child.id),
        age: Math.max(0, toNumber(child.age, 0)),
        flag_with_bed: child.withBed,
        sequence_number: index + 1,
      })),
    }

    const trimmedSourceOther = quotationSourceOther.trim()
    const otherSourceOption = sourceOptions.find((opt) => isQuotationSourceOtherName(opt.label))
    const otherSourceId = otherSourceOption ? String(otherSourceOption.value) : ""
    if (trimmedSourceOther && otherSourceId) {
      payload.quotation_source_type_id = Number(otherSourceId)
      payload.quotation_source_other = trimmedSourceOther
    } else if (quotationSourceTypeId && quotationSourceTypeId !== otherSourceId) {
      payload.quotation_source_type_id = Number(quotationSourceTypeId)
      payload.quotation_source_other = null
    }

    try {
      const response = await agent.QuotationBasicDetailsAPI.put(payload)
      if (!response?.status) {
        const apiErrorMessage = response?.error?.message || "Failed to save quotation basic details"
        setApiResponseMessage({ type: "error", text: apiErrorMessage })
        showError(apiErrorMessage)
        return null
      }

      const savedQuotationId = response?.data?.quotation?.quotation_id || response?.data?.[0]?.quotation_id || quotationId || ""
      if (savedQuotationId && onSaved) {
        onSaved(savedQuotationId)
      }
      const successMessage = intent === "ready to send" ? "Quotation finalized successfully" : "Quotation basic details saved successfully"
      setApiResponseMessage({ type: "success", text: successMessage })
      showSuccess(successMessage)
      return savedQuotationId || null
    } catch (error) {
      setApiResponseMessage({ type: "error", text: "Failed to save quotation basic details" })
      showError("Failed to save quotation basic details")
      return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quotationId,
    staCode,
    quotationName,
    startDate,
    noOfNights,
    agentId,
    agencyId,
    allAgents,
    quotationSourceTypeId,
    quotationSourceOther,
    sourceOptions,
    adults,
    extraBed,
    children,
    rooms,
    flagVisaFee,
    foc,
    focPaxNumber,
    visaFeePerPax,
    selectedCities,
    childDetails,
    onSaved,
    showError,
    showSuccess,
  ])

  useEffect(() => {
    if (!onBindSaveHandler) return
    onBindSaveHandler(saveBasicDetails)
  }, [onBindSaveHandler, saveBasicDetails])

  return (
    <div className={styles.contentCard}>
      <h2 className={styles.sectionTitle}>Basic Details</h2>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Select
            label="Source"
            value={quotationSourceTypeId}
            onChange={(e) => setQuotationSourceTypeId(e.target.value)}
            placeholder="Select source"
            options={sourceOptions}
            isOtherOption={isSourceOtherSelectOption}
            otherValue={quotationSourceOther}
            onOtherChange={setQuotationSourceOther}
            otherPlaceholder="Other"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Select
            label="Agency"
            required={true}
            value={agencyId}
            onChange={(e) => setAgencyId(e.target.value)}
            placeholder="Select agency"
            options={agenciesOptions}
            error={showValidationErrors ? validationErrors.agency : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Select
            label="Agent"
            required={true}
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            placeholder="Select agent"
            options={agentsOptions}
            error={showValidationErrors ? validationErrors.agent : undefined}
          />
          {(() => {
            const selectedAgent = allAgents.find((a) => a.agent_id === agentId)
            if (!selectedAgent) return null
            const phone = String(selectedAgent.contact_number || "").trim()
            const email = String(selectedAgent.email || "").trim()
            if (!phone && !email) return null
            return (
              <div className={styles.cityInfoText} style={{ marginTop: 6, lineHeight: 1.35 }}>
                {phone && <span style={{ color: "#7a7a7a" }}>{phone}</span>}
                {phone && email && <span style={{ color: "#7a7a7a" }}> , </span>}
                {email && <span style={{ color: "#7a7a7a" }}>{email}</span>}
              </div>
            )
          })()}
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <DatePicker
            label="Start Date"
            required={true}
            value={startDate}
            onChange={setStartDate}
            placeholder="dd/mm/yyyy"
            leftIcon={<IconsColor iconName="calendarIcon" width="18px" height="18px" fillColor="none" strokeColor="#6B7280" />}
            error={showValidationErrors ? validationErrors.startDate : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputText
            label="No. of Nights"
            required={true}
            type="number"
            value={noOfNights}
            onChange={(e) => handleNightsChange(e.target.value, setNoOfNights)}
            error={showValidationErrors ? validationErrors.noOfNights : undefined}
          />
        </Grid>
      </Grid>

      <div className={styles.citySection}>
        <div className={styles.citySectionHeader}>
          <h3 className={styles.citySectionTitle}>Cities & Nights (Sequential Order)</h3>
          <Button type="outlined" label="+ Add City" onClick={addCity} />
        </div>
        <div className={styles.citySummary}>
          <span>{`Total Nights: ${totalCityNights} | End Date: ${computedEndDate}`}</span>
        </div>

        <Grid container spacing={2}>
          {selectedCities.map((row, index) => (
            <Grid key={row.id} size={{ xs: 12, md: 4 }}>
              <div className={styles.cityCard}>
                <div className={styles.cityFields}>
                  <div className={styles.citySelect}>
                    <Select
                      label={`City ${index + 1}`}
                      value={row.city}
                      onChange={(e) => updateCityRow(row.id, "city", e.target.value)}
                      placeholder="Select city"
                      options={getRowCityOptions(row.city)}
                      height="40px"
                      error={showValidationErrors || cityFieldTouched[row.id]?.city ? validationErrors.cityRows[row.id]?.city : undefined}
                    />
                  </div>
                  <div className={styles.cityNights}>
                    <InputText
                      label="Nights"
                      type="number"
                      value={row.nights}
                      height="40px"
                      noMargin={true}
                      onChange={(e) => updateCityRow(row.id, "nights", e.target.value === "" ? "" : String(Math.max(0, Number(e.target.value) || 0)))}
                      error={showValidationErrors || cityFieldTouched[row.id]?.nights ? validationErrors.cityRows[row.id]?.nights : undefined}
                    />
                  </div>
                </div>
                <button className={styles.deleteCityButton} type="button" onClick={() => removeCity(row.id)} disabled={selectedCities.length <= 1} title={selectedCities.length <= 1 ? "At least one city is required" : "Remove city"}>
                  <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor={selectedCities.length <= 1 ? "#9CA3AF" : "#D32F2F"} />
                </button>
              </div>
            </Grid>
          ))}
        </Grid>
        {selectedCities.length < 1 && <p className={styles.cityInfoText}>At least one city is required.</p>}
        {validationErrors.totalNights && <p className={styles.cityInfoText} style={{ color: "#D32F2F" }}>{validationErrors.totalNights}</p>}
      </div>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputText
            label="No. of Adults"
            required={true}
            type="number"
            value={adults}
            onChange={(e) => handleAdultsCountChange(e.target.value)}
            error={showValidationErrors ? validationErrors.adults : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputText label="No. of Extra Bed" type="number" value={extraBed} onChange={(e) => handleNightsChange(e.target.value, setExtraBed)} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputText label="No. of Children" type="number" value={children} onChange={(e) => handleChildrenCountChange(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <InputText label="No. of Rooms (Default)" type="number" value={rooms} onChange={(e) => handleNightsChange(e.target.value, setRooms)} />
        </Grid>
      </Grid>

      {childDetails.length > 0 && (
        <div className={styles.childrenSection}>
          <h3 className={styles.childrenSectionTitle}>Children Details</h3>
          <Grid container spacing={2}>
            {childDetails.map((child, index) => (
              <Grid key={child.id} size={{ xs: 12, md: 3 }}>
                {/* <div className={styles.childCard}> */}
                {/* <p className={styles.childCardTitle}>Child {index + 1}</p> */}
                {/* <div className={styles.childInlineControls}> */}
                <Grid container spacing={2} className={styles.childCard}>
                  <Grid size={{ xs: 12, md: 7 }}>
                    {/* <div className={styles.childAgeField}> */}
                    <InputText
                      label={`Child ${index + 1} : Age`}
                      type="number"
                      value={child.age}
                      height="36px"
                      noMargin={true}
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === "") {
                          updateChildDetailRow(child.id, "age", "")
                          return
                        }
                        const numericValue = Number(value)
                        if (Number.isNaN(numericValue) || numericValue < 0) return
                        updateChildDetailRow(child.id, "age", String(Math.floor(numericValue)))
                      }}
                    // width="100%"
                    />
                    {/* </div> */}
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <div className={styles.childCheckbox}>
                      <Checkbox label="With Bed" checked={child.withBed} onChange={(checked) => updateChildDetailRow(child.id, "withBed", checked)} />
                    </div>
                  </Grid>
                </Grid>
                {/* </div> */}
                {/* </div> */}
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      <div className={styles.checkboxRow}>
        <Checkbox label="Include Visa Fee" checked={flagVisaFee} onChange={setFlagVisaFee} />
        <Checkbox label="Foc" checked={foc} onChange={setFoc} />
      </div>

      {(flagVisaFee || foc) && (
        <Grid container spacing={2}>
          {flagVisaFee && (
            <Grid size={{ xs: 12, md: 3 }}>
              <InputText label="Visa Fee per Pax (VND)" type="number" value={visaFeePerPax} onChange={(e) => handleNightsChange(e.target.value, setVisaFeePerPax)} />
            </Grid>
          )}
          {foc && (
            <Grid size={{ xs: 12, md: 3 }}>
              <InputText label="Foc Pax Number" type="number" value={focPaxNumber} onChange={(e) => handleNightsChange(e.target.value, setFocPaxNumber)} />
            </Grid>
          )}
        </Grid>
      )}
      {apiResponseMessage && (
        <p className={styles.cityInfoText} style={{ color: apiResponseMessage.type === "success" ? "#15803D" : "#D32F2F" }}>
          {apiResponseMessage.text}
        </p>
      )}
    </div>
  )
}

export default QuotationBasicDetails
